import React from 'react';
import PropTypes from 'prop-types';

import Curtain from './../Curtain';
import { CDN_URL } from '../../constants';
import * as Utils from '../../utils';
import ScrollButton from '../ScrollButton';

const Letter = ({ curtain, ...rest }) => {
  const handler = () => {
    Utils.scroll('letter-from-the-president');
  };

  return (
    <div className='letter' {...rest}>
      <Curtain {...curtain} handler={handler} />
      <div className='letter__container'>
        <div className='letter__main'>
          <div className='container'>
            <div className='row'>
              <div className='col'>
                <div className='letter__main-container'>
                  <div className='letter__header'>
                    <div className='letter__metadata'>
                      <div className='letter__subtitle'>Letter from the President</div>
                      <div className='letter__author'>Reihan Salam</div>
                    </div>
                    <h2 className='letter__title'>
                      <strong>Dear Friends and Supporters,</strong> <br />
                      On issue after issue, we’re living through a retreat from reality.
                    </h2>
                  </div>
                  <div className='letter__body'>
                    <img
                      src={`${CDN_URL}/assets/images/image-3.jpg`}
                      alt=''
                      className='float-lg-start'
                    />
                    <p>
                      Faced with soaring energy costs, Congress has embraced policies that will
                      drive energy prices even higher, threatening American prosperity and our
                      nation’s geopolitical edge. Cities across the country have responded to an
                      alarming increase in violent crime by, among other things, curbing traffic
                      enforcement and reducing penalties for carjacking, policies that are sure to
                      make matters worse. Leading universities are announcing that they will no
                      longer require applicants to submit standardized test scores, part of a
                      concerted effort to dismantle merit-based admissions. Medical schools and
                      schools of public health are racing to embed pseudoscientific race
                      essentialism in their curricula and teaching, despite the baleful consequences
                      for patients. Why are we seeing so many institutions embracing this kind of
                      destructive ideological overreach? One gets the distinct impression that it is
                      not the long-term consequences of these policies that matter—it’s whether
                      they’re fashionable in the moment. And if you dare to run afoul of today’s
                      reigning orthodoxy, prepare to be shouted down.
                    </p>
                    <p>
                      At a time when elite discourse is dominated by intellectual conformism and the
                      ever-present threat of cancellation, the Manhattan Institute is a home for
                      scholars, journalists, activists, and civic leaders who will follow the truth
                      wherever it leads. That often means taking a stand against the conventional
                      wisdom. Drawing on research, reporting, and analysis of the highest caliber,
                      our team works to improve the quality of life, overcome ethnic and cultural
                      divides, promote educational excellence, and expand economic freedom in
                      America and its great cities. In the pages that follow, you’ll see how we’ve
                      changed the national conversation on all these issues and more.
                    </p>
                    <p>
                      We begin with the woke takeover of American institutions. Schools,
                      corporations, and other organizations across the country have embraced a
                      radical ideology in which America is built on oppression and where classical
                      liberal values such as individual rights are merely manifestations of bigotry.
                      In 2022, several of our most prolific scholars, including Heather Mac Donald
                      and Christopher Rufo, dedicated their talents to exposing this problem and
                      devising solutions in the arts, medicine, and education. You’ll find a
                      thought-provoking Q&A with Rufo within the{' '}
                      <a
                        href='#driving-the-national-debate'
                        onClick={() =>
                          Utils.handleScrollToSlide('driving-the-national-debate', 0, 'qa')
                        }
                      >
                        “Pursuing Truth in the Face of Woke Ideology”
                      </a>{' '}
                      section.
                    </p>
                    <p>
                      Next, MI has been very active lately in the realm of legal policy, thanks in
                      part to the invaluable addition of Ilya Shapiro as our director of
                      constitutional studies. In addition to publishing thought-provoking commentary
                      on the major cases of the day, Shapiro is overseeing a new program to file
                      amicus briefs directly to the courts. Further, many members of MI’s staff,
                      including our new book fellow Gail Heriot, have taken a special interest in
                      the Supreme Court’s affirmative-action cases and will be keenly attuned to the
                      next steps if the Court forbids the use of race in admissions.
                    </p>
                    <blockquote>
                      <p>
                        “At a time when elite discourse is dominated by intellectual conformism and
                        the ever-present threat of cancellation, the Manhattan Institute is a home
                        for scholars, journalists, activists, and civic leaders who will follow the
                        truth wherever it leads.”
                      </p>
                    </blockquote>
                    <p>
                      Next, we delve into our work on policing and public safety, which has focused
                      on the recent increase in violent crime amid efforts to shrink the
                      criminal-justice system, from activist campaigns to defund the police to the
                      rise of a progressive prosecution movement that all too often prioritizes
                      decarceration over public safety. The release of Rafael Mangual’s book{' '}
                      <i>Criminal (In)Justice</i> was a major highlight of the year, and you can see
                      an illuminating Q&A with him in the{' '}
                      <a
                        href='#driving-the-national-debate-2'
                        onClick={() =>
                          Utils.handleScrollToSlide('driving-the-national-debate', 2, 'qa')
                        }
                      >
                        “Making the Case for Effective Law Enforcement”
                      </a>{' '}
                      section. Mangual and his colleagues also published a series of reports
                      exposing the public-safety drawbacks of numerous recent policy changes,
                      including New York’s bail reform and “Raise the Age” laws.
                    </p>
                    <p>
                      Beyond our work on New York’s criminal-justice system, we have taken on the
                      Big Apple’s housing crisis, the struggles its schools face as enrollment
                      declines, and the ways in which its election rules stifle political
                      competition, in a wide range of reports and articles. In addition to being
                      America’s most populous city, New York has long served as a seedbed of civic
                      and ideological innovation. Policy approaches and political movements that
                      begin in New York tend to spread to other urban centers, and that’s why we’re
                      so committed to the city’s renewal.
                    </p>
                    <img
                      src={`${CDN_URL}/assets/images/image-4.jpg`}
                      alt=''
                      className='float-lg-start'
                    />
                    <p>
                      The last year has been especially noteworthy when it comes to our own media
                      efforts. <i>City Journal</i>, our urban-affairs magazine, averages more than
                      half a million users per month, posts three to four articles a day, and is
                      frequently cited across other media. And 2022 saw the launch of numerous
                      podcasts from our fellows to complement <i>CJ’s</i> own <i>10 Blocks</i>{' '}
                      podcast, including <i>The Last Optimist</i> from Mark Mills,{' '}
                      <i>Risk Talking</i> from Allison Schrager, and <i>Institutionalized</i> from
                      Charles Fain Lehman. What’s more, Glenn Loury’s long-running podcast,{' '}
                      <i>The Glenn Show</i>, made MI its new home in 2022. Podcasts launching in
                      2023 include CRT—that is, <i>Christopher Rufo Theory</i>—and{' '}
                      <i>Manhattan Insights</i>, hosted by me and a number of MI scholars.
                    </p>
                    <p>
                      Last but not least, in an era of high inflation and failing banks, MI
                      continues to lead the way in economic policy. MI’s Brian Riedl and Allison
                      Schrager have consistently sounded the alarm about the country’s debt levels
                      and pushed for realistic reforms. Jason Riley published his masterful book{' '}
                      <i>The Black Boom</i>, a look at improvements in the economic lives of black
                      Americans during the Trump administration. Mark Mills has been tireless in
                      making the case for energy realism in the face of reckless and delusional
                      green energy policies. And MI’s Adam Smith Society, celebrating its tenth
                      year, continues to build a network of remarkable MBA students, young
                      professionals, and business leaders who are committed to strengthening the
                      foundational role of the free-market in driving growth and opportunity in the
                      United States.
                    </p>

                    <p>
                      This report concludes with a big-picture summary of what we’ve accomplished
                      this past year, including lists of our books and events, as well as numerous
                      statistics, such as how many op-eds we placed (641) and our total number of
                      media hits (9,275). Don’t miss the new additions to our list of fellows,
                      including the Harvard economist Roland Fryer, who specializes in topics
                      ranging from policing to education, and Leor Sapir, who has quickly
                      distinguished himself on the topic of the ideological capture of American
                      medicine.
                    </p>
                    <p>
                      We’re able to do all these things only because we have such generous
                      supporters who share our vision for a better future, and for that, we thank
                      you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='letter__footer'>
          <div className='container'>
            <div className='row'>
              <div className='col'>
                <div className='letter__footer-container'>
                  <div className='letter__footer-col-1'>
                    <img
                      src={`${CDN_URL}/assets/images/reihan-salam.jpg`}
                      alt='Reihan Salam'
                      className='letter__image'
                      loading='lazy'
                    />
                  </div>
                  <div className='letter__footer-col-2'>
                    <div className='letter__closing'>Sincerely,</div>
                    <div className='letter__signature' />
                    <img
                      src={`${CDN_URL}/assets/images/signature.png`}
                      alt='Reihan Salam'
                      className='letter__signature'
                      loading='lazy'
                    />
                    <div className='letter__name'>Reihan Salam</div>
                    <div className='letter__role'>President</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='letter__cta'>
          <ScrollButton href='#driving-the-national-debate' theme='dark' />
        </div>
      </div>
    </div>
  );
};

Letter.propTypes = {
  curtain: PropTypes.object,
  id: PropTypes.string,
};

export default Letter;
